import React, { ReactNode, createContext, useContext, useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./Tabs.module.css";
import { IconType } from "react-icons";

// --- Context ---
interface TabsContextType {
  activeIndex: number;
  onChange: (index: number) => void;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = (): TabsContextType => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs subcomponents must be rendered within a <Tabs> parent.");
  }
  return context;
};

// --- Types ---
export interface TabsProps {
  children: ReactNode;
  /** Index of the currently active tab (Controlled mode) */
  activeIndex?: number;
  /** Callback fired when a tab is selected (Controlled mode) */
  onChange?: (index: number) => void;
  /** Default active tab index (Uncontrolled mode). Ignored if activeIndex is set. */
  defaultActiveIndex?: number;
  className?: string;
  style?: React.CSSProperties;
}

export interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export interface TabsTabProps {
  /** Optional Material Symbol name or React Component */
  icon?: string | IconType;
  /** Tab text label */
  label: ReactNode;
  /** Disable the tab */
  disabled?: boolean;
  className?: string;
  /** Internal prop injected by TabsList */
  index?: number; 
}

export interface TabsPanelsProps {
  children: ReactNode;
  className?: string;
}

export interface TabsPanelProps {
  children: ReactNode;
  className?: string;
  /** Internal prop injected by TabsPanels */
  index?: number;
}

// --- Helpers ---
const renderIcon = (icon: string | IconType | undefined): React.ReactNode => {
  if (!icon) return null;
  if (typeof icon === "string") {
    if (icon.startsWith("pi ") || icon.startsWith("fas ") || icon.startsWith("far ")) {
      return <i className={classNames(icon, styles.iconBase)} aria-hidden="true" />;
    }
    return <span className={classNames(styles.iconBase, "material-symbols-outlined")} aria-hidden="true">{icon}</span>;
  }
  const IconComponent = icon as IconType;
  return <IconComponent className={styles.iconBase} aria-hidden="true" />;
};

// --- Components ---

const Root: React.FC<TabsProps> = ({ 
  children, 
  activeIndex: controlledIndex, 
  onChange: controlledOnChange,
  defaultActiveIndex = 0,
  className,
  style
}) => {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultActiveIndex);

  const isControlled = controlledIndex !== undefined;
  const activeIndex = isControlled ? controlledIndex : uncontrolledIndex;

  const handleChange = (index: number): void => {
    if (!isControlled) {
      setUncontrolledIndex(index);
    }
    if (controlledOnChange) {
      controlledOnChange(index);
    }
  };

  return (
    <TabsContext.Provider value={{ activeIndex, onChange: handleChange }}>
      <div className={classNames(styles.tabsContainer, className)} style={style}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const List: React.FC<TabsListProps> = ({ children, className }) => {
  const tabs = React.Children.toArray(children).filter(React.isValidElement);

  return (
    <div className={classNames(styles.tabListWrapper, className)} role="tablist">
      <div className={styles.tabList}>
        {tabs.map((child, idx) => 
          React.cloneElement(child, { index: idx } as any)
        )}
      </div>
    </div>
  );
};

const Tab: React.FC<TabsTabProps> = ({ icon, label, disabled, className, index }) => {
  const { activeIndex, onChange } = useTabsContext();
  const isActive = activeIndex === index;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      className={classNames(
        styles.tabButton,
        {
          [styles.tabButtonActive]: isActive,
          [styles.tabButtonDisabled]: disabled
        },
        className
      )}
      onClick={() => {
        if (!disabled && index !== undefined) {
          onChange(index);
        }
      }}
    >
      {renderIcon(icon)}
      <span className={styles.tabLabel}>{label}</span>
      {isActive && <div className={styles.tabActiveIndicator} />}
    </button>
  );
};

const Panels: React.FC<TabsPanelsProps> = ({ children, className }) => {
  const panels = React.Children.toArray(children).filter(React.isValidElement);

  return (
    <div className={classNames(styles.tabPanels, className)}>
      {panels.map((child, idx) => 
        React.cloneElement(child, { index: idx } as any)
      )}
    </div>
  );
};

const Panel: React.FC<TabsPanelProps> = ({ children, className, index }) => {
  const { activeIndex } = useTabsContext();

  if (activeIndex !== index) {
    return null;
  }

  return (
    <div role="tabpanel" className={classNames(styles.tabPanel, className)}>
      {children}
    </div>
  );
};

// --- Compound Export ---
export const Tabs = Object.assign(Root, {
  List,
  Tab,
  Panels,
  Panel
});

export default Tabs;
